import { memo, useMemo } from 'react';
import { Typography } from '@components/base';
import { IOrderDetailsTypes, PerformaInvoiceStates } from '@helpers/types/pi';
import { TRANSPORTATION_MODE, INCO_TERMS, DELIVERY_TERMS } from '@helpers/constants';
import css from '../index.module.scss';
import { sanitizeValue, shouldShowPorts } from '@helpers/utils';

interface OrderProps {
  previewData: PerformaInvoiceStates;
}

interface OrderFields {
  key: keyof IOrderDetailsTypes | string;
  label: string;
  value?: (data: IOrderDetailsTypes) => string | number | null;
  visible?: (data: IOrderDetailsTypes) => boolean;
}

const ORDER_FIELDS: OrderFields[] = [
  {
    key: 'transportation_mode',
    label: 'Mode of Transportation',
    value: (d: any) =>
      TRANSPORTATION_MODE.find((i) => i.value === d.transportation_mode)?.label ?? '-'
  },
  {
    key: 'inco_terms',
    label: 'Inco Terms',
    value: (d: any) => INCO_TERMS.find((i) => i.value === d.inco_terms)?.label ?? '-'
  },
  { key: 'country_of_origin', label: 'Country of Origin' },
  { key: 'country_of_final_destination', label: 'Country of Final Destination' },
  {
    key: 'port_of_loading_display_value',
    label: 'Port of Loading',
    value: (d: any) =>
      d.port_of_loading_display_value && d.port_of_loading_country_display_value
        ? `${d.port_of_loading_display_value} - ${d.port_of_loading_country_display_value}`
        : '-',
    visible: (d: any, supplyType?: string) =>
      shouldShowPorts(supplyType) && !!d.port_of_loading_display_value
  },
  {
    key: 'port_of_discharge_display_value',
    label: 'Port of Discharge',
    value: (d: any) =>
      d.port_of_discharge_display_value && d.port_of_discharge_country_display_value
        ? `${d.port_of_discharge_display_value} - ${d.port_of_discharge_country_display_value}`
        : '-',
    visible: (d: any, supplyType?: string) =>
      shouldShowPorts(supplyType) && !!d.port_of_discharge_display_value
  },
  { key: 'place_of_delivery', label: 'Place of Delivery' },
  { key: 'payment_terms', label: 'Payment Terms' },
  {
    key: 'delivery_terms',
    label: 'Delivery Terms',
    value: (d: any) => DELIVERY_TERMS.find((i) => i.value === d.inco_terms)?.label ?? '-'
  },
  { key: 'place_of_quality_test', label: 'Quality Testing' },
  {
    key: 'is_tnc_not_needed',
    label: 'T&C Accepted',
    value: (d: any) => (d.is_tnc_not_needed ? 'Yes' : 'No')
  }
];

const Order = (props: OrderProps) => {
  const { previewData } = props;
  const { orderDetails, orderInfo } = previewData;

  const supplyType = orderInfo.order_type;

  const visibleFields = useMemo(
    () =>
      ORDER_FIELDS.filter(({ visible }) =>
        typeof visible === 'function' ? visible(orderDetails) : true
      ),
    [orderDetails]
  );

  return (
    <div className={css.boxWrapper}>
      <div className={css.orderDetailsContainer}>
        <div className={css.fieldTitle}>
          <Typography variant="pdoc">Order Details</Typography>
        </div>

        <div className={css.gridContainer}>
          {visibleFields.map(({ key, label, value }) => {
            const raw =
              typeof value === 'function'
                ? value(orderDetails)
                : orderDetails[key as keyof IOrderDetailsTypes] ?? '-';

            const list = Array.isArray(raw) ? raw : [raw];

            return (
              <div key={key} className={css.columnWrapper}>
                <Typography variant="label">{label}</Typography>
                {list.map((v, i) => (
                  <Typography variant="body" key={i}>
                    {sanitizeValue(v)}
                  </Typography>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default memo(Order);
